'use client';

import React from 'react';
import * as SubframeUtils from '../utils';
import { Avatar } from './Avatar';

interface ProfileInfo {
  label: string;
  value: React.ReactNode;
}

interface NavigationItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  active?: boolean;
  onClick?: () => void;
}

interface ProfileNavigationCardProps extends React.HTMLAttributes<HTMLDivElement> {
  userName: string;
  userEmail: string;
  userImage?: string;
  infos?: ProfileInfo[];
  navigationItems: NavigationItem[];
  className?: string;
}

const NavigationItemComponent = React.forwardRef<
  HTMLButtonElement,
  {
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    active?: boolean;
    onClick?: () => void;
  }
>(function NavigationItemComponent({ label, icon: Icon, active = false, onClick }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      className={SubframeUtils.twClassNames(
        'flex w-full items-center gap-3 rounded-lg border-none px-4 py-3 text-left transition-all duration-200',
        'focus-visible:ring-brand-600/10 focus:outline-none focus-visible:ring-4',
        {
          'bg-neutral-50 text-neutral-900': active,
          'bg-white text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900': !active,
        },
      )}
    >
      <Icon
        className={SubframeUtils.twClassNames('h-4 w-4 flex-shrink-0', {
          'text-neutral-900': active,
          'text-neutral-500': !active,
        })}
      />
      <span
        className={SubframeUtils.twClassNames('text-sm font-medium', {
          'text-neutral-900': active,
          'text-neutral-600': !active,
        })}
      >
        {label}
      </span>
    </button>
  );
});

NavigationItemComponent.displayName = 'NavigationItem';

export const ProfileNavigationCard = React.forwardRef<HTMLDivElement, ProfileNavigationCardProps>(
  function ProfileNavigationCard(
    { userName, userEmail, userImage, infos = [], navigationItems, className, ...otherProps },
    ref,
  ) {
    return (
      <div
        ref={ref}
        className={SubframeUtils.twClassNames(
          'flex h-fit w-full flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm',
          className,
        )}
        {...otherProps}
      >
        {/* Header - User Info */}
        <div className="flex flex-col gap-4 p-6">
          <div className="flex items-center gap-4">
            <Avatar size="large" className="h-12 w-12 text-lg" variant="neutral" image={userImage}>
              {!userImage && userName.substring(0, 1).toUpperCase()}
            </Avatar>
            <div className="flex flex-col overflow-hidden">
              <span className="truncate text-base font-bold text-neutral-900" title={userName}>
                {userName}
              </span>
              <span className="truncate text-sm text-neutral-500" title={userEmail}>
                {userEmail}
              </span>
            </div>
          </div>
        </div>

        {/* Separator */}
        <div className="w-full pt-1 pb-2">
          <div className="h-px w-full bg-neutral-200"></div>
        </div>

        {/* Info Section */}
        {infos.length > 0 && (
          <>
            <div className="flex flex-col gap-4 p-6">
              {infos.map((info, index) => (
                <div key={index} className="flex flex-col gap-2">
                  <span className="text-sm text-neutral-500">{info.label}</span>
                  <div className="text-sm font-medium text-neutral-900">{info.value}</div>
                </div>
              ))}
            </div>
            {/* Separator */}
            <div className="w-full pt-1 pb-2">
              <div className="h-px w-full bg-neutral-200"></div>
            </div>
          </>
        )}

        {/* Navigation Items */}
        <nav className="flex flex-col gap-1 p-4">
          {navigationItems.map((item) => (
            <NavigationItemComponent
              key={item.id}
              label={item.label}
              icon={item.icon}
              active={item.active}
              onClick={item.onClick}
            />
          ))}
        </nav>
      </div>
    );
  },
);

ProfileNavigationCard.displayName = 'ProfileNavigationCard';
